import { compareDecimalStrings, isDecimalString } from "@/shared/utils/decimal";
import { formatRail } from "@/shared/utils/railDisplay";

import {
  PaymentDirection,
  PaymentFeeKind,
  type FeeRowStatus,
  type FeeScope,
  type PaymentFee,
  type ProviderRailRoute,
} from "../types/paymentFees.types";
import { isZeroDecimal, tierRanges } from "./feeMath";

/**
 * Display helpers for the price book. Everything here is presentation only — the numbers themselves come from
 * `feeMath.ts` as exact decimal strings and are formatted through `Intl` without a `Number` round-trip on the
 * value's precision (`formatMoneyText` keeps up to 8 fraction digits).
 *
 * Labels are injected rather than translated in place: these are pure functions, so they cannot reach
 * `useTranslations`.
 */

/**
 * Money for inline text (a formula line, a CSV-ish label). For standalone amounts prefer
 * `ColocaAmountDisplay`.
 */
export function formatMoneyText(value: string, locale: string): string {
  if (!isDecimalString(value)) return value;
  // Intl.NumberFormat v3 formats decimal strings exactly (no double-precision loss on 8-dp amounts); the TS
  // ES2022 lib still types format() as number-only, hence the assertion — same as ColocaAmountDisplay.
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 8,
  }).format(value as unknown as number);
}

/** Percentage with up to 4 fraction digits and no grouping — `0.75%`, `1.125%`. */
export function formatPercentText(value: string, locale: string): string {
  if (!isDecimalString(value)) return `${value}%`;
  return `${new Intl.NumberFormat(locale, {
    maximumFractionDigits: 4,
    useGrouping: false,
  }).format(value as unknown as number)}%`;
}

/** Threshold label for a tier row — integers read better without forced decimals. */
export function formatThresholdText(threshold: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 8 }).format(threshold);
}

/** An amount in running text, without forced decimals: `1.000.000`, `0,5`. */
export function formatAmountText(value: string, locale: string): string {
  if (!isDecimalString(value)) return value;
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 8 }).format(value as unknown as number);
}

export interface FeeFormulaLabels {
  /** Shown in place of a currency code when the rule applies to any currency. */
  anyCurrency: string;
  min: string;
  max: string;
  ranges: (count: number) => string;
  rangeFromTo: (from: string, to: string) => string;
  rangeFrom: (from: string) => string;
}

/** `0.75% + 8 MXN`, with an explicit `0 MXN` when both terms are zero: no fee is a deliberate configuration. */
export function formatPricePair(percentage: string, fixed: string, currency: string, locale: string): string {
  const parts: string[] = [];
  if (isDecimalString(percentage) && !isZeroDecimal(percentage)) {
    parts.push(formatPercentText(percentage, locale));
  }
  if (isDecimalString(fixed) && !isZeroDecimal(fixed)) {
    parts.push(`${formatAmountText(fixed, locale)} ${currency}`);
  }
  return parts.join(" + ") || `0 ${currency}`;
}

/** `0.75% + 8 MXN · min 5 · max 250`, or `3 amount ranges · min 5` for a rule priced by ranges. */
export function formatFeeFormula(fee: PaymentFee, locale: string, labels: FeeFormulaLabels): string {
  const currency = fee.currency ?? labels.anyCurrency;
  let formula =
    fee.tiers && fee.tiers.length > 0
      ? labels.ranges(tierRanges(fee).length)
      : formatPricePair(fee.percentage, fee.fixed, currency, locale);
  if (isDecimalString(fee.min_fee) && !isZeroDecimal(fee.min_fee)) {
    formula += ` · ${labels.min} ${formatAmountText(fee.min_fee, locale)}`;
  }
  if (fee.max_fee != null && fee.max_fee !== "" && isDecimalString(fee.max_fee)) {
    formula += ` · ${labels.max} ${formatAmountText(fee.max_fee, locale)}`;
  }
  return formula;
}

export interface FeeRangeLine {
  label: string;
  price: string;
  isBase: boolean;
}

/** One line per band of the operation amount: `1.000 to under 1.000.000 COP` → `0.5%`. */
export function formatRangeLines(
  fee: Pick<PaymentFee, "fixed" | "percentage" | "tiers" | "currency">,
  locale: string,
  labels: FeeFormulaLabels,
): FeeRangeLine[] {
  const currency = fee.currency ?? labels.anyCurrency;
  return tierRanges(fee).map((range) => ({
    label: `${
      range.to == null
        ? labels.rangeFrom(formatAmountText(range.from, locale))
        : labels.rangeFromTo(formatAmountText(range.from, locale), formatAmountText(range.to, locale))
    } ${currency}`,
    price: formatPricePair(range.percentage, range.fixed, currency, locale),
    isBase: range.isBase,
  }));
}

export interface ScopeChipLabels {
  general: string;
  anyCostRoute: string;
  anyCountry: string;
  anyRail: string;
  anyCurrency: string;
  anyDirection: string;
  payin: string;
  payout: string;
  /** Receives how many active routes currently use this provider. */
  costRoute: (corridorCount: number) => string;
  /** The rule pins a provider whose code matches no active route today. */
  costRouteInactive: string;
}

export interface ScopeChip {
  id: string;
  label: string;
  /** `true` for an "applies to any …" dimension — rendered dashed and muted so generality is visible. */
  muted: boolean;
  /** Organization names must not render in monospace; codes and enum values should. */
  mono: boolean;
}

/**
 * The six scope dimensions as chips, general → specific.
 *
 * The provider dimension never renders `provider_code`: project rule 12 keeps provider identity out of the
 * UI, and payments-activity / payout-rescue both hold that line. Instead a `PROVIDER_COST` rule shows how
 * many corridors currently route through it, which is the information an operator actually needs and is
 * derived from the routes catalog.
 */
export function scopeChipsFor(
  fee: PaymentFee,
  labels: ScopeChipLabels,
  routes: ProviderRailRoute[],
  organizationNames: Record<string, string> = {},
): ScopeChip[] {
  const chips: ScopeChip[] = [];

  if (fee.fee_kind === PaymentFeeKind.COLOCA_COMMISSION) {
    const name = fee.organization_id ? organizationNames[fee.organization_id] : undefined;
    chips.push({
      id: "organization",
      label: fee.organization_id ? (name ?? shortId(fee.organization_id)) : labels.general,
      muted: fee.organization_id == null,
      mono: fee.organization_id != null && name == null,
    });
  } else if (fee.provider_code) {
    const corridorCount = routes.filter((route) => route.provider_code === fee.provider_code).length;
    chips.push({
      id: "route",
      label: corridorCount > 0 ? labels.costRoute(corridorCount) : labels.costRouteInactive,
      muted: false,
      mono: false,
    });
  } else {
    chips.push({ id: "route", label: labels.anyCostRoute, muted: true, mono: false });
  }

  chips.push({
    id: "country",
    label: fee.country ?? labels.anyCountry,
    muted: fee.country == null,
    mono: fee.country != null,
  });
  chips.push({
    id: "rail",
    label: fee.rail ? formatRail(fee.rail) : labels.anyRail,
    muted: fee.rail == null,
    mono: false,
  });
  chips.push({
    id: "currency",
    label: fee.currency ?? labels.anyCurrency,
    muted: fee.currency == null,
    mono: fee.currency != null,
  });
  chips.push({
    id: "direction",
    label:
      fee.direction == null
        ? labels.anyDirection
        : fee.direction === PaymentDirection.PAYIN
          ? labels.payin
          : labels.payout,
    muted: fee.direction == null,
    mono: false,
  });

  return chips;
}

/**
 * Same chip list for a scope that is not yet a row (the create dialog's locked-scope summary). Builds a
 * throwaway fee-shaped object so both paths share one implementation and can never drift.
 */
export function scopeChipsForScope(
  feeKind: PaymentFeeKind,
  scope: FeeScope,
  labels: ScopeChipLabels,
  routes: ProviderRailRoute[],
  organizationNames: Record<string, string> = {},
): ScopeChip[] {
  return scopeChipsFor(
    {
      fee_kind: feeKind,
      provider_code: scope.provider_code,
      organization_id: scope.organization_id,
      rail: scope.rail,
      country: scope.country,
      currency: scope.currency,
      direction: scope.direction,
    } as PaymentFee,
    labels,
    routes,
    organizationNames,
  );
}

/** `MEX · SPEI · MXN · Payout` — the corridor identity used in selectors, chips and warnings. */
export function corridorLabel(
  route: ProviderRailRoute,
  directionLabels: { payin: string; payout: string },
): string {
  const direction =
    route.direction === PaymentDirection.PAYIN ? directionLabels.payin : directionLabels.payout;
  return `${route.country} · ${formatRail(route.rail)} · ${route.currency} · ${direction}`;
}

/** Corridor without the direction — the filter groups both directions under one option. */
export function corridorFilterKey(route: ProviderRailRoute): string {
  return `${route.country}|${route.rail}|${route.currency}`;
}

/** UUID tail, for when a name catalog is unavailable. The full id always goes in a `title`. */
export function shortId(id: string): string {
  return `${id.slice(0, 8)}…`;
}

/** Derived status → `ColocaStatusPill` variant. `SUPERSEDED`/`EXPIRED` stay neutral: history, not failure. */
export const FEE_STATUS_PILL_VARIANT: Record<
  FeeRowStatus,
  "success" | "info" | "neutral" | "danger" | "warning"
> = {
  ACTIVE: "success",
  SCHEDULED: "info",
  SUPERSEDED: "neutral",
  EXPIRED: "neutral",
  DISABLED: "danger",
};

/**
 * Money for the authoring preview: the same Intl treatment as `formatMoneyText`, plus an explicit `+` when
 * `signed`. The margin line must read as a gain or a loss from the sign alone, never from colour only.
 */
export function formatPreviewAmount(value: string | null, locale: string, signed = false): string | null {
  if (value == null) return null;
  const formatted = formatMoneyText(value, locale);
  if (!signed) return formatted;
  return compareDecimalStrings(value, "0") > 0 ? `+${formatted}` : formatted;
}
